import { Box, Button, Paper, Typography, useMediaQuery } from '@mui/material';
import Pusher, { Channel, PresenceChannel } from 'pusher-js';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useVideoCall } from '../../../contexts/videoCall/context';
import { VideoCallActions } from '../../../contexts/videoCall/types';
import { parseDateRange } from '../../../helpers/time';
import appointmentsServiceClient from '../../../lib/appointments/client';
import { MeetingUserType } from '../../../lib/appointments/types';
import { APPOINTMENTS_BASE_URL, PUSHER_APP_KEY, PUSHER_CLUSTER } from '../../../lib/environment';
import { logErrorAndReport } from '../../../lib/errorReporting';
import { getRoomUuidFromChannel } from '../../../lib/format';
import MixpanelClient, { TrackingEventV2 } from '../../../lib/tracking/mixpanel';
import { UserType } from '../../../lib/video/types';
import {
  MeetingInformation as ChimeMeetingInformation,
  MeetingPusherEventName,
  PusherClientEvent,
  PusherMember,
  PusherPresenceMembers,
  PusherSystemEvent,
} from '../../ChimeVideoCall/meeting-call.types';
import Toast from '../../Toast/Toast';
import JoiningCallModal from './JoiningCallModal';
import styles from './ParticipantVideoPreview.module.css';
import VideoPreview from './VideoPreview';

const POLLING_TIME_MS = 5000;

const ParticipantVideoPreview: FC = () => {
  const { dispatchVideoCall, videoCallState } = useVideoCall();

  const { appointment, eventsChannel, currentParticipant, attendant, host } =
    videoCallState.videoPreview ?? {};

  const { acuityAppointmentTimezone, appointmentEndAt, appointmentStartAt } = appointment ?? {};
  const appointmentDateRangeAsString = parseDateRange({
    endDate: appointmentEndAt,
    startDate: appointmentStartAt,
    timezone: acuityAppointmentTimezone,
  });

  const [hasPermissions, setHasPermissions] = useState<boolean>(false);
  const [readyToJoinCall, setReadyToJoinCall] = useState<boolean>(false);
  const [attendantHasJoinedTheCall, setAttendantHasJoinedTheCall] = useState<boolean>(false);
  const [connectedBy, setConnectedBy] = useState<string>('');
  const pollingIntervalRef = useRef<NodeJS.Timer | null>(null);

  const amIHost = currentParticipant?.userType === 'HOST';
  const isMobile = useMediaQuery('(max-width:600px)');

  const stopPolling = useCallback(() => {
    if (!pollingIntervalRef.current) {
      return;
    }

    clearInterval(pollingIntervalRef.current);
    pollingIntervalRef.current = null;
  }, []);

  const joinCall = useCallback(async () => {
    if (!videoCallState.videoPreview) {
      Toast.error('Could not join the meeting');
      return;
    }

    const { token, currentParticipant, appointment, host, attendant, source, eventsChannel } =
      videoCallState.videoPreview;

    try {
      const result = await appointmentsServiceClient.joinMeetingAsParticipant(token);
      if (result?.videoConfig) {
        stopPolling();

        MixpanelClient.trackEvent({
          eventName: TrackingEventV2.VideoCallLocalJoinRoom,
          properties: {
            source,
            connectedBy,
            appointmentId: appointment.coreAppointmentId,
            waitingRoomUuid: getRoomUuidFromChannel(eventsChannel),
            participantUuid: currentParticipant.uuid,
          },
        });

        const userType =
          currentParticipant.userType === MeetingUserType.Host
            ? UserType.Provider
            : UserType.Member;

        const baseMeetingInfo = {
          videoConfig: result.videoConfig,
          appointment: {
            id: appointment.coreAppointmentId,
            type: appointment.appointmentTypeName,
          },
        };

        let meetingInfo: ChimeMeetingInformation;

        if (userType === UserType.Provider) {
          meetingInfo = {
            ...baseMeetingInfo,
            userType,
            meetingAttendees: {
              provider: {
                id: host.id,
                name: host.name,
                authToken: currentParticipant.authToken,
              },
              member: {
                id: attendant.id,
                name: attendant.name,
                authToken: '',
                clinicId: currentParticipant.clinicId ?? 0,
              },
            },
          };
        } else {
          meetingInfo = {
            ...baseMeetingInfo,
            userType,
            meetingAttendees: {
              provider: {
                id: host.id,
                name: host.name,
              },
              member: {
                id: currentParticipant.id,
                name: currentParticipant.name,
                authToken: currentParticipant.authToken,
                clinicId: currentParticipant.clinicId ?? 0,
              },
            },
          };
        }

        dispatchVideoCall({
          type: VideoCallActions.ChimeSetCallInfo,
          payload: meetingInfo,
        });
      }
    } catch (error) {
      logErrorAndReport({ error });
      Toast.error('Could not join the meeting');
    }
  }, [videoCallState.videoPreview, stopPolling, connectedBy, dispatchVideoCall]);

  const handleClickOnJoinCall = useCallback(() => {
    setReadyToJoinCall(true);
    // Try to join the call by making a request. If it succeeds then we start the call,
    // otherwise we wait until we receive some event on the event channel.

    if (videoCallState.videoPreview) {
      const { appointment, eventsChannel, source, currentParticipant } =
        videoCallState.videoPreview;

      MixpanelClient.trackEvent({
        eventName: TrackingEventV2.VideoCallLocalWaitingAttendant,
        properties: {
          source,
          appointmentId: appointment.coreAppointmentId,
          waitingRoomUuid: getRoomUuidFromChannel(eventsChannel),
          participantUuid: currentParticipant.uuid,
        },
      });
    }

    joinCall();
  }, [joinCall, videoCallState.videoPreview]);

  // TODO: When pusher notification presence channel is tested and working fine
  // check this function  maybe is not necessary use a polling with this new change (presence channel events)
  const checkHostHasConnected = useCallback(async () => {
    if (!videoCallState.videoPreview) {
      Toast.error(`Can't check the participants connection`);
      return;
    }

    const { token } = videoCallState.videoPreview;
    const result = await appointmentsServiceClient.getMeetingInformation(token!);

    if (!result) {
      // For now, we show nothing as we make this request several times.
      return;
    }

    const { participants } = result;
    if (!amIHost) {
      const { host: hostInfo } = result;

      const hostParticipant = participants.find((p) => {
        return p.userType !== MeetingUserType.Attendant && p.uuid === hostInfo.uuid;
      });

      if (hostParticipant?.connectedAt && readyToJoinCall) {
        setConnectedBy('polling');

        stopPolling();
        joinCall();
      }
    }
  }, [joinCall, videoCallState.videoPreview, stopPolling, readyToJoinCall, amIHost]);

  // Setup Pusher connection (stable dependencies)
  useEffect(() => {
    let pusher: Pusher | null = null;
    let channel: Channel | null = null;
    let presenceChannel: PresenceChannel | null = null;

    if (eventsChannel) {
      pusher = new Pusher(PUSHER_APP_KEY, {
        cluster: PUSHER_CLUSTER,
        authEndpoint: `${APPOINTMENTS_BASE_URL}/rtm/auth`,
        auth: {
          params: { participantUuid: currentParticipant!.uuid },
        },
      });

      pusher.connection.bind('error', (error: any) => {
        logErrorAndReport({ error });
      });

      presenceChannel = pusher.subscribe(`presence-${eventsChannel}`) as PresenceChannel;

      presenceChannel.bind(
        PusherSystemEvent.SubscriptionSucceeded,
        (presenceMembers: PusherPresenceMembers) => {
          presenceChannel!.trigger(PusherClientEvent.ClientJoined, {
            participantUuid: currentParticipant!.uuid,
            name: currentParticipant!.name,
            ts: Date.now(),
          });

          if (presenceMembers.count === 1) {
            // Only the current participant nothing to do.
            return;
          }

          const attendant = videoCallState.videoPreview?.attendant;
          if (attendant && presenceMembers.members) {
            const attendantIsPresent = Object.keys(presenceMembers.members).find(
              (id) => id === attendant!.uuid
            );

            if (attendantIsPresent) {
              setAttendantHasJoinedTheCall(true);
            }
          }
        }
      );

      presenceChannel.bind(PusherSystemEvent.MemberRemoved, (member: PusherMember) => {
        if (attendant?.uuid === member.id) {
          setAttendantHasJoinedTheCall(false);
        }
      });

      presenceChannel.bind(PusherSystemEvent.MemberAdded, (member: PusherMember) => {
        if (attendant?.uuid === member.id) {
          setAttendantHasJoinedTheCall(true);
        }
      });

      channel = pusher.subscribe(eventsChannel);
      channel.bind(PusherSystemEvent.Error, (status: any) => {
        console.error('SUB ERROR (public channel):', status);
      });

      if (!amIHost) {
        channel.bind(MeetingPusherEventName.HostHasJoinedTheCall, () => {
          if (readyToJoinCall) {
            setConnectedBy('pusher');
            joinCall();
          }
        });
      } else {
        channel.bind(MeetingPusherEventName.AttendantHasAskedToJoinTheCall, () => {
          setAttendantHasJoinedTheCall(true);
        });
      }
    }

    return () => {
      if (channel && pusher && presenceChannel) {
        channel.unbind_all();
        channel.unsubscribe();

        presenceChannel.unbind_all();
        presenceChannel.unsubscribe();

        // Only disconnect if the connection is still open
        if (pusher.connection.state === 'connected' || pusher.connection.state === 'connecting') {
          pusher.disconnect();
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsChannel, amIHost, videoCallState.videoPreview]);

  // Setup polling separately
  useEffect(() => {
    pollingIntervalRef.current = setInterval(checkHostHasConnected, POLLING_TIME_MS);

    return () => {
      stopPolling();
    };
  }, [checkHostHasConnected, stopPolling]);

  return (
    <>
      <Box className={styles.videoPreviewContainer}>
        <Paper className={styles.videoPreviewContent}>
          <Typography className={styles.videoPreviewTitle}>
            Welcome, {currentParticipant?.name ?? 'Member'}
          </Typography>
          <Typography variant='body2'>{appointmentDateRangeAsString}</Typography>

          {amIHost && attendantHasJoinedTheCall && (
            <Box className={styles.attendantConnectStatusContainer}>
              <Box className={styles.attendantStatus} />
              <Typography variant='body2'>{attendant?.name}</Typography>
            </Box>
          )}

          <VideoPreview hasPermissions={hasPermissions} setHasPermissions={setHasPermissions} />

          {!amIHost && (
            <>
              <Typography variant='body2' className={styles.textBalance}>
                {' '}
                Press <b>Join Call</b> below to connect with your provider.
              </Typography>
            </>
          )}

          {!isMobile && (
            <Button variant='contained' onClick={handleClickOnJoinCall} disabled={!hasPermissions}>
              {amIHost ? `Start Call` : `Join Call`}
            </Button>
          )}
        </Paper>
      </Box>

      <JoiningCallModal open={readyToJoinCall && !amIHost} host={host} />

      {isMobile && (
        <Box className={styles.bottomSection}>
          <Box className={styles.bottomSectionContent}>
            <Button
              variant='contained'
              onClick={handleClickOnJoinCall}
              fullWidth
              disabled={!hasPermissions}>
              {amIHost ? `Start Call` : `Join Call`}
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ParticipantVideoPreview;
